//! Voice/TTS related functions
// Voice analysis + ElevenLabs/R2-backed Text-to-Speech playback.
import { INTERVIEW_API_BASE_URL, getAccessToken, getAuthHeaders } from '../../config/api';

export const setupVoiceAnalysis = async (micStreamRef, audioContextRef, analyserRef, setVoiceMetrics) => {
    try {
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true
            }
        });
        micStreamRef.current = stream;

        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioContext;
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(stream);

        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.8;
        microphone.connect(analyser);
        analyserRef.current = analyser;

        return analyser;
    } catch (error) {
        console.error('Microphone access error:', error);
        throw error;
    }
};

export const analyzeVoice = (analyserRef, setVoiceMetrics, isRecording, interviewState, waitingForStart) => {
    if (!analyserRef.current) return null;

    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const timeDataArray = new Uint8Array(bufferLength);

    const analyze = () => {
        analyser.getByteFrequencyData(dataArray);
        analyser.getByteTimeDomainData(timeDataArray);

        // Calculate volume (RMS)
        let sum = 0;
        for (let i = 0; i < timeDataArray.length; i++) {
            const normalized = (timeDataArray[i] - 128) / 128;
            sum += normalized * normalized;
        }
        const rms = Math.sqrt(sum / timeDataArray.length);
        const volume = Math.min(100, Math.round(rms * 300));

        // Calculate pitch (dominant frequency)
        let maxValue = 0;
        let maxIndex = 0;
        for (let i = 0; i < bufferLength; i++) {
            if (dataArray[i] > maxValue) {
                maxValue = dataArray[i];
                maxIndex = i;
            }
        }
        const pitch = Math.round((maxIndex / bufferLength) * 100);

        // Calculate energy
        const energy = Math.round((dataArray.reduce((a, b) => a + b, 0) / bufferLength / 255) * 100);

        setVoiceMetrics({ volume, pitch, energy });

        return requestAnimationFrame(analyze);
    };

    return analyze();
};

export const stopVoiceAnalysis = (animationFrameRef, micStreamRef, audioContextRef) => {
    if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
    }
    if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current) {
        audioContextRef.current.close();
    }
};

let activeTtsAudio = null;

const getQuestionText = (value) => {
    if (value && typeof value === 'object') return String(value.question || value.text || '').trim();
    return String(value || '').trim();
};

const getAudioUrl = (value) => {
    if (!value || typeof value !== 'object') return '';
    return value.audio_url || value.audioUrl || value.url || '';
};

export const stopSpeaking = () => {
    if (activeTtsAudio) {
        activeTtsAudio.pause();
        activeTtsAudio.currentTime = 0;
        activeTtsAudio = null;
    }
};

export const playAudioUrl = (audioUrl, onEnd) => {
    stopSpeaking();

    if (!audioUrl) {
        if (typeof onEnd === 'function') onEnd();
        return null;
    }

    const audio = new Audio(audioUrl);
    activeTtsAudio = audio;

    const finish = () => {
        if (activeTtsAudio === audio) activeTtsAudio = null;
        if (typeof onEnd === 'function') onEnd();
    };

    audio.addEventListener('ended', finish, { once: true });
    audio.addEventListener('error', finish, { once: true });

    audio.play().catch((error) => {
        console.error('ElevenLabs audio playback failed:', error);
        finish();
    });

    return audio;
};

// Speak with cached ElevenLabs audio. Questions returned by /generate-questions already
// contain audio_url. For greeting/farewell/runtime text, this calls /speech-audio,
// which generates once, uploads to R2, then returns the cached public URL.
export const speakText = async (textOrQuestion, language, voiceGender, onEnd) => {
    const directUrl = getAudioUrl(textOrQuestion);
    if (directUrl) {
        return playAudioUrl(directUrl, onEnd);
    }

    const text = getQuestionText(textOrQuestion);
    if (!text) {
        if (typeof onEnd === 'function') onEnd();
        return null;
    }

    const token = getAccessToken();
    if (!token) {
        console.warn('Login required to fetch ElevenLabs audio. Skipping TTS playback.');
        if (typeof onEnd === 'function') onEnd();
        return null;
    }

    try {
        const response = await fetch(`${INTERVIEW_API_BASE_URL}/speech-audio`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
                text,
                namespace: 'runtime-tts',
                language,
                voiceGender,
            }),
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.audio_url) {
            console.warn('No cached ElevenLabs audio returned:', data?.error || response.status);
            if (typeof onEnd === 'function') onEnd();
            return null;
        }

        return playAudioUrl(data.audio_url, onEnd);
    } catch (error) {
        console.error('Failed to fetch ElevenLabs audio:', error);
        if (typeof onEnd === 'function') onEnd();
        return null;
    }
};
